import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useClipboard } from '@/hooks/useClipboard'
import { api, startPlatformConnect } from '@/lib/api'

// ─── Constants ────────────────────────────────────────────────────────────────

export const ONBOARDING_COMPLETE_KEY = 'kast_onboarding_complete'
const ONBOARDING_PROGRESS_KEY = 'kast_onboarding_progress'

type StepId = 1 | 2 | 3 | 4 | 5

const TOTAL_STEPS = 5

const PLATFORM_COLORS: Record<string, string> = {
  twitch: '#9146FF',
  youtube: '#FF0000',
  kick: '#53FC18',
  tiktok: '#ffffff',
}

const PLATFORMS = [
  { id: 'twitch', name: 'Twitch', icon: '🟣', color: '#9146FF' },
  { id: 'youtube', name: 'YouTube', icon: '🔴', color: '#FF0000' },
  { id: 'kick', name: 'Kick', icon: '🟢', color: '#53FC18' },
  { id: 'tiktok', name: 'TikTok', icon: '⬛', color: '#ffffff', comingSoon: true },
] as const

type PlatformId = (typeof PLATFORMS)[number]['id']

// ─── localStorage progress shape ─────────────────────────────────────────────

interface OnboardingProgress {
  step: StepId
  selectedPlatforms: PlatformId[]
  connectedPlatforms: PlatformId[]
  displayName: string
  streamTitle: string
  alertStyle: string
  overlayTheme: string
  chatMode: string
  streamKey: string | null
  rtmpUrl: string | null
}

const DEFAULT_PROGRESS: OnboardingProgress = {
  step: 1,
  selectedPlatforms: [],
  connectedPlatforms: [],
  displayName: '',
  streamTitle: '',
  alertStyle: '',
  overlayTheme: '',
  chatMode: '',
  streamKey: null,
  rtmpUrl: null,
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface StreamKeyResponse {
  id: string
  platform: string
  stream_key?: string
  ingest_url?: string | null
  custom_ingest_url?: string | null
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

const btnPrimary =
  'w-full py-3 px-6 rounded-xl font-semibold text-white transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400 flex items-center justify-center gap-2'
const btnGhost =
  'w-full py-2.5 px-6 rounded-xl font-semibold text-sm border transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400'

// ─── Step progress bar ────────────────────────────────────────────────────────

function StepBar({ current, total }: { current: StepId; total: number }) {
  const STEP_LABELS = ['Welcome', 'Platforms', 'Stream Key', 'Customize', "Ready!"]
  return (
    <div className="flex items-center justify-between gap-1 px-2" role="tablist" aria-label="Onboarding progress">
      {Array.from({ length: total }, (_, i) => {
        const step = (i + 1) as StepId
        const isActive = step === current
        const isPast = step < current
        return (
          <div key={step} className="flex flex-col items-center flex-1">
            <div
              role="tab"
              aria-selected={isActive}
              aria-label={`Step ${step}: ${STEP_LABELS[i]}`}
              style={{
                height: 3,
                borderRadius: 2,
                backgroundColor: isPast ? '#a78bfa' : isActive ? 'var(--accent)' : '#1e1e3a',
                transition: 'background-color 0.3s ease',
              }}
              className="w-full mb-1"
            />
            <span
              className="text-[9px] font-medium tracking-wide hidden sm:block"
              style={{ color: isActive ? '#a78bfa' : isPast ? '#6d5ca0' : '#2d2d4a' }}
            >
              {STEP_LABELS[i]}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Slide wrapper ────────────────────────────────────────────────────────────

function SlidePane({
  children,
  direction,
}: {
  children: React.ReactNode
  direction: 'left' | 'right' | 'none'
}) {
  const animClass =
    direction === 'left'
      ? 'animate-slide-in-left'
      : direction === 'right'
        ? 'animate-slide-in-right'
        : ''
  return <div className={animClass}>{children}</div>
}

// ─── Step 1: Welcome ──────────────────────────────────────────────────────────

function StepWelcome({
  displayName,
  onDisplayNameChange,
  selectedPlatforms,
  onPlatformToggle,
  onNext,
}: {
  displayName: string
  onDisplayNameChange: (v: string) => void
  selectedPlatforms: PlatformId[]
  onPlatformToggle: (id: PlatformId) => void
  onNext: () => void
}) {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col items-center text-center mb-6">
        <div
          className="flex items-center justify-center w-14 h-14 rounded-2xl mb-4 text-white font-bold text-2xl shadow-lg"
          style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)' }}
          aria-hidden="true"
        >
          K
        </div>
        <h1 className="text-2xl font-bold text-white mb-1 tracking-tight">Welcome to Kast</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Let&rsquo;s get you streaming in under 5 minutes.
        </p>
      </div>

      {/* Display name */}
      <div className="mb-5">
        <label
          htmlFor="ob-display-name"
          className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
          style={{ color: 'var(--text-muted)' }}
        >
          Your name
        </label>
        <input
          id="ob-display-name"
          type="text"
          value={displayName}
          onChange={(e) => onDisplayNameChange(e.target.value)}
          placeholder="How should Kast greet you?"
          maxLength={50}
          className="w-full rounded-xl border px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none transition-colors"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: '#1e1e3a' }}
          onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = 'var(--accent)' }}
          onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = '#1e1e3a' }}
        />
      </div>

      {/* Platform selection */}
      <div className="mb-6">
        <p className="text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Which platforms do you stream on?
        </p>
        <div className="grid grid-cols-2 gap-2">
          {PLATFORMS.map((p) => {
            const selected = selectedPlatforms.includes(p.id)
            const color = PLATFORM_COLORS[p.id] ?? '#9ca3af'
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => !p.comingSoon && onPlatformToggle(p.id)}
                disabled={p.comingSoon}
                className="flex items-center gap-2.5 rounded-xl border p-3 text-left transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400 disabled:cursor-default disabled:opacity-50"
                style={{
                  backgroundColor: selected ? `${color}12` : 'var(--bg-card)',
                  borderColor: selected ? `${color}55` : '#1e1e3a',
                }}
                aria-pressed={selected}
                aria-label={p.comingSoon ? `${p.name} (coming soon)` : `${p.name}${selected ? ', selected' : ''}`}
              >
                <span className="text-lg shrink-0" aria-hidden="true">{p.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white leading-tight">{p.name}</p>
                  {p.comingSoon && (
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Coming soon</p>
                  )}
                </div>
                <div
                  className="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all"
                  style={{
                    borderColor: selected ? color : 'rgba(255,255,255,0.2)',
                    backgroundColor: selected ? color : 'transparent',
                  }}
                  aria-hidden="true"
                >
                  {selected && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={onNext}
        className={btnPrimary}
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}
      >
        Get started
      </button>
    </div>
  )
}

// ─── Step 2: Connect platforms ────────────────────────────────────────────────

function StepConnectPlatforms({
  selectedPlatforms,
  connectedPlatforms,
  onConnectedChange,
  onNext,
  onSkip,
  onBack,
}: {
  selectedPlatforms: PlatformId[]
  connectedPlatforms: PlatformId[]
  onConnectedChange: (ids: PlatformId[]) => void
  onNext: () => void
  onSkip: () => void
  onBack: () => void
}) {
  const [connecting, setConnecting] = useState<string | null>(null)
  const [connectError, setConnectError] = useState<string | null>(null)

  // Show the platforms the user selected + any already connected ones
  const displayPlatforms = PLATFORMS.filter(
    (p) => selectedPlatforms.includes(p.id) || connectedPlatforms.includes(p.id)
  )
  // Fallback: if user selected nothing, show all non-coming-soon platforms
  const platformsToShow = displayPlatforms.length > 0
    ? displayPlatforms
    : PLATFORMS.filter((p) => !p.comingSoon)

  const handleConnect = useCallback(async (id: PlatformId) => {
    if (connectedPlatforms.includes(id)) return
    if (id === 'tiktok') return
    setConnecting(id)
    setConnectError(null)
    sessionStorage.setItem('kast_onboarding_resume_step', '2')
    try {
      await startPlatformConnect(id as 'twitch' | 'youtube' | 'kick')
      onConnectedChange([...connectedPlatforms, id])
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : `Couldn't connect ${id}. Try again.`)
    } finally {
      setConnecting(null)
    }
  }, [connectedPlatforms, onConnectedChange])

  const anyConnected = connectedPlatforms.length > 0

  return (
    <div className="flex flex-col">
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-white mb-1">Connect your platforms</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Authorize each platform so Kast can multistream on your behalf.
        </p>
      </div>

      {connectError && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300 text-xs">
          {connectError}
        </div>
      )}

      <div className="flex flex-col gap-2.5 mb-5">
        {platformsToShow.map((p) => {
          const color = PLATFORM_COLORS[p.id] ?? '#9ca3af'
          const isConnected = connectedPlatforms.includes(p.id)
          const isConnecting = connecting === p.id
          const isComingSoon = p.comingSoon
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => !isConnected && !isConnecting && !isComingSoon && handleConnect(p.id)}
              disabled={isConnected || isConnecting || isComingSoon}
              className="flex items-center gap-3 w-full rounded-xl border p-3.5 text-left transition-all disabled:cursor-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
              style={{
                backgroundColor: isConnected ? `${color}12` : 'var(--bg-card)',
                borderColor: isConnected ? `${color}55` : '#1e1e3a',
              }}
              aria-label={isConnected ? `${p.name} connected` : isComingSoon ? `${p.name} (coming soon)` : `Connect ${p.name}`}
            >
              <span
                className="w-9 h-9 rounded-lg flex items-center justify-center text-lg shrink-0"
                style={{ backgroundColor: `${color}22` }}
                aria-hidden="true"
              >
                {p.icon}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{p.name}</p>
                <p className="text-xs mt-0.5" style={{ color: isConnected ? color : 'var(--text-muted)' }}>
                  {isConnected ? 'Connected' : isConnecting ? 'Connecting...' : isComingSoon ? 'Coming soon' : 'Click to authorize'}
                </p>
              </div>
              {isConnected ? (
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full text-xs text-white font-bold shrink-0"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                >
                  ✓
                </span>
              ) : isConnecting ? (
                <span className="shrink-0 text-gray-500"><Spinner /></span>
              ) : (
                <span className="shrink-0 text-gray-600 text-sm" aria-hidden="true">→</span>
              )}
            </button>
          )
        })}
      </div>

      <button
        type="button"
        onClick={onNext}
        className={btnPrimary}
        style={{
          background: anyConnected
            ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
            : 'rgba(124,58,237,0.4)',
        }}
      >
        {anyConnected ? 'Continue' : 'Continue anyway'}
      </button>

      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={onBack}
          className={btnGhost + ' flex-1'}
          style={{ borderColor: '#1e1e3a', color: '#a78bfa', backgroundColor: 'transparent' }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="flex-1 py-2.5 px-6 rounded-xl text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
          style={{ color: 'var(--text-muted)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
        >
          Skip for now
        </button>
      </div>
    </div>
  )
}

// ─── Step 3: Set Up Your Stream ───────────────────────────────────────────────

const RTMP_INGEST_URL = 'rtmp://ingest.kast.video/live'

function StepStreamKey({
  streamKey,
  rtmpUrl,
  onKeysLoaded,
  onNext,
  onBack,
}: {
  streamKey: string | null
  rtmpUrl: string | null
  onKeysLoaded: (key: string, url: string) => void
  onNext: () => void
  onBack: () => void
}) {
  const { copy, copied } = useClipboard()
  const [loading, setLoading] = useState(!streamKey)
  const [fetchError, setFetchError] = useState<string | null>(null)

  useEffect(() => {
    if (streamKey) return
    setLoading(true)
    setFetchError(null)
    api.get<StreamKeyResponse[]>('/keys')
      .then((keys) => {
        if (!Array.isArray(keys) || keys.length === 0) return
        const first = keys[0]
        if (first?.stream_key) {
          const url = first.custom_ingest_url ?? first.ingest_url ?? RTMP_INGEST_URL
          onKeysLoaded(first.stream_key, url)
        }
      })
      .catch((err) => {
        setFetchError(err instanceof Error ? err.message : 'Could not load stream key.')
      })
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ingestUrl = rtmpUrl ?? RTMP_INGEST_URL

  return (
    <div className="flex flex-col">
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-white mb-1">Set up your stream</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Copy these into OBS or any RTMP encoder. You only need to do this once.
        </p>
      </div>

      {fetchError && (
        <div className="mb-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-yellow-300 text-xs">
          {fetchError} — you can find your key later in Settings &rsaquo; Stream Keys.
        </div>
      )}

      {/* RTMP Server */}
      <div className="mb-3">
        <p className="text-xs font-medium mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          RTMP Server / URL
        </p>
        <div
          className="flex items-center gap-2 rounded-xl border p-3"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: '#1e1e3a' }}
        >
          <code className="flex-1 text-purple-300 text-xs font-mono truncate select-all">
            {ingestUrl}
          </code>
          <button
            type="button"
            onClick={() => copy(ingestUrl)}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
            style={{ backgroundColor: 'rgba(124,58,237,0.2)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.3)' }}
            aria-label="Copy RTMP URL"
          >
            Copy
          </button>
        </div>
      </div>

      {/* Stream Key */}
      <div className="mb-4">
        <p className="text-xs font-medium mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Stream key
        </p>
        <div
          className="flex items-center gap-2 rounded-xl border p-3"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: '#1e1e3a' }}
        >
          {loading ? (
            <div className="flex items-center gap-2 flex-1">
              <Spinner />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</span>
            </div>
          ) : streamKey ? (
            <code className="flex-1 text-purple-300 text-xs font-mono truncate select-all">
              {'•'.repeat(Math.min(streamKey.length, 28))}
            </code>
          ) : (
            <span className="flex-1 text-xs italic" style={{ color: 'var(--text-muted)' }}>
              No key found — generate one in Settings &rsaquo; Stream Keys
            </span>
          )}
          {streamKey && (
            <button
              type="button"
              onClick={() => copy(streamKey)}
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
              style={
                copied
                  ? { backgroundColor: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }
                  : { backgroundColor: 'rgba(124,58,237,0.2)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.3)' }
              }
              aria-label={copied ? 'Copied' : 'Copy stream key'}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          )}
        </div>
        <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
          Treat this like a password — keep it private.
        </p>
      </div>

      {/* OBS guide */}
      <div
        className="flex items-center gap-3 rounded-xl border p-4 mb-5"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: '#1e1e3a' }}
      >
        <span className="text-2xl shrink-0" aria-hidden="true">📹</span>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium">OBS setup guide</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Settings &rarr; Stream &rarr; Service: Custom &rarr; paste server + key above.
          </p>
        </div>
        <a
          href="/docs/obs-setup"
          className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
          style={{ color: '#a78bfa', backgroundColor: 'rgba(124,58,237,0.12)' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          Full guide
        </a>
      </div>

      <button
        type="button"
        onClick={onNext}
        className={btnPrimary}
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}
      >
        Continue
      </button>
      <button
        type="button"
        onClick={onBack}
        className={btnGhost + ' mt-2'}
        style={{ borderColor: '#1e1e3a', color: '#a78bfa', backgroundColor: 'transparent' }}
      >
        Back
      </button>
    </div>
  )
}

// ─── Step 4: Customize ────────────────────────────────────────────────────────

const ALERT_STYLES = [
  { id: 'minimal', label: 'Minimal', icon: '✦', desc: 'Clean & subtle' },
  { id: 'bold', label: 'Bold', icon: '⚡', desc: 'Big & energetic' },
  { id: 'retro', label: 'Retro', icon: '🕹️', desc: 'Pixel art vibes' },
  { id: 'neon', label: 'Neon', icon: '🌌', desc: 'Glowing & bright' },
]

const OVERLAY_THEMES = [
  { id: 'dark', label: 'Dark', icon: '◾', desc: 'Sleek dark UI' },
  { id: 'purple', label: 'Purple', icon: '🟣', desc: 'Kast signature' },
  { id: 'green', label: 'Green', icon: '🟢', desc: 'Streamer classic' },
  { id: 'minimal', label: 'Minimal', icon: '◽', desc: 'Distraction-free' },
]

const CHAT_MODES = [
  { id: 'unified', label: 'Unified', icon: '💬', desc: 'All platforms merged' },
  { id: 'split', label: 'Split view', icon: '⊞', desc: 'Per-platform tabs' },
  { id: 'moderated', label: 'Moderated', icon: '🛡️', desc: 'Automod on by default' },
]

function PickGrid<T extends { id: string; label: string; icon: string; desc: string }>({
  items,
  selected,
  onSelect,
}: {
  items: readonly T[]
  selected: string
  onSelect: (id: string) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((item) => {
        const isSelected = selected === item.id
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            className="flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
            style={{
              backgroundColor: isSelected ? 'rgba(124,58,237,0.12)' : 'var(--bg-card)',
              borderColor: isSelected ? 'rgba(124,58,237,0.5)' : '#1e1e3a',
            }}
            aria-pressed={isSelected}
          >
            <div className="flex items-center gap-2 w-full">
              <span className="text-base" aria-hidden="true">{item.icon}</span>
              <span className="text-sm font-semibold text-white flex-1">{item.label}</span>
              {isSelected && (
                <div
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: 'var(--accent)' }}
                  aria-hidden="true"
                >
                  <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 12 12">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
          </button>
        )
      })}
    </div>
  )
}

function StepCustomize({
  alertStyle,
  overlayTheme,
  chatMode,
  onAlertStyleChange,
  onOverlayThemeChange,
  onChatModeChange,
  onNext,
  onBack,
}: {
  alertStyle: string
  overlayTheme: string
  chatMode: string
  onAlertStyleChange: (v: string) => void
  onOverlayThemeChange: (v: string) => void
  onChatModeChange: (v: string) => void
  onNext: () => void
  onBack: () => void
}) {
  return (
    <div className="flex flex-col">
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-white mb-1">Customize your stream</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Pick your look — you can fine-tune everything later.
        </p>
      </div>

      <div className="space-y-5">
        <div>
          <p className="text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Alert style
          </p>
          <PickGrid
            items={ALERT_STYLES}
            selected={alertStyle}
            onSelect={onAlertStyleChange}
          />
        </div>

        <div>
          <p className="text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Overlay theme
          </p>
          <PickGrid
            items={OVERLAY_THEMES}
            selected={overlayTheme}
            onSelect={onOverlayThemeChange}
          />
        </div>

        <div>
          <p className="text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Chat display
          </p>
          <PickGrid
            items={CHAT_MODES}
            selected={chatMode}
            onSelect={onChatModeChange}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 mt-6">
        <button
          type="button"
          onClick={onNext}
          className={btnPrimary}
          style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}
        >
          {alertStyle || overlayTheme || chatMode ? "Save & continue" : "Skip customization"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className={btnGhost}
          style={{ borderColor: '#1e1e3a', color: '#a78bfa', backgroundColor: 'transparent' }}
        >
          Back
        </button>
      </div>
    </div>
  )
}

// ─── Step 5: You're Ready ─────────────────────────────────────────────────────

interface ChecklistItem {
  label: string
  done: boolean
}

function StepReady({
  progress,
  onGoLive,
  onDashboard,
}: {
  progress: OnboardingProgress
  onGoLive: () => void
  onDashboard: () => void
}) {
  const checklist: ChecklistItem[] = [
    { label: 'Account created', done: true },
    { label: `Platform${progress.connectedPlatforms.length !== 1 ? 's' : ''} connected (${progress.connectedPlatforms.length > 0 ? progress.connectedPlatforms.join(', ') : 'none'})`, done: progress.connectedPlatforms.length > 0 },
    { label: 'RTMP stream key ready', done: !!progress.streamKey },
    { label: `Alert style: ${progress.alertStyle || 'default'}`, done: !!progress.alertStyle },
    { label: `Overlay theme: ${progress.overlayTheme || 'default'}`, done: !!progress.overlayTheme },
    { label: `Chat display: ${progress.chatMode || 'default'}`, done: !!progress.chatMode },
  ]

  const doneCount = checklist.filter((c) => c.done).length

  return (
    <div className="flex flex-col items-center text-center">
      <div
        className="flex items-center justify-center w-16 h-16 rounded-full mb-5 text-3xl"
        style={{
          background: 'linear-gradient(135deg, rgba(124,58,237,0.25) 0%, rgba(167,139,250,0.15) 100%)',
          border: '2px solid #7c3aed',
        }}
        aria-hidden="true"
      >
        🎬
      </div>

      <h2 className="text-2xl font-bold text-white mb-1">You&rsquo;re ready to go live!</h2>
      <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
        {doneCount} of {checklist.length} things set up.{' '}
        {doneCount < checklist.length && 'You can complete the rest later.'}
      </p>

      {/* Checklist */}
      <div
        className="w-full rounded-xl border mb-5 text-left"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: '#1e1e3a' }}
      >
        <p
          className="text-xs font-medium uppercase tracking-wider px-4 py-3"
          style={{ color: 'var(--text-muted)', borderBottom: '1px solid #1e1e3a' }}
        >
          Setup checklist
        </p>
        <ul className="divide-y" style={{ borderColor: '#1e1e3a' }}>
          {checklist.map((item) => (
            <li
              key={item.label}
              className="flex items-center gap-3 px-4 py-2.5"
            >
              <span
                className="flex items-center justify-center w-5 h-5 rounded-full shrink-0 text-xs font-bold"
                style={{
                  backgroundColor: item.done ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)',
                  color: item.done ? '#4ade80' : '#4b5563',
                  border: `1px solid ${item.done ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.08)'}`,
                }}
                aria-hidden="true"
              >
                {item.done ? '✓' : '–'}
              </span>
              <span
                className="text-xs"
                style={{ color: item.done ? 'var(--text-secondary)' : '#4b5563' }}
              >
                {item.label}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-2 w-full mb-6">
        {[
          { label: 'Docs', href: '/docs', icon: '📖' },
          { label: 'Discord', href: 'https://discord.gg/kast', icon: '💬', external: true },
          { label: 'Alerts', href: '/alerts', icon: '🔔' },
          { label: 'Overlays', href: '/overlays', icon: '🎨' },
        ].map(({ label, href, icon, external }) => (
          <a
            key={label}
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className="flex items-center gap-2 rounded-xl border p-3 text-left transition-all hover:border-purple-500/40"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: '#1e1e3a' }}
          >
            <span aria-hidden="true">{icon}</span>
            <span className="text-xs font-medium text-white">{label}</span>
          </a>
        ))}
      </div>

      <button
        type="button"
        onClick={onGoLive}
        className={btnPrimary + ' mb-2 shadow-lg shadow-purple-500/20'}
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}
      >
        Go Live
      </button>

      <button
        type="button"
        onClick={onDashboard}
        className={btnGhost}
        style={{ borderColor: '#1e1e3a', color: '#a78bfa', backgroundColor: 'transparent' }}
        onMouseEnter={(e) => {
          ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent)'
          ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(124,58,237,0.08)'
        }}
        onMouseLeave={(e) => {
          ;(e.currentTarget as HTMLButtonElement).style.borderColor = '#1e1e3a'
          ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
        }}
      >
        Explore Dashboard
      </button>
    </div>
  )
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

export function OnboardingWizard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [, setComplete] = useLocalStorage<boolean>(ONBOARDING_COMPLETE_KEY, false)
  const [savedProgress, setSavedProgress] = useLocalStorage<OnboardingProgress>(
    ONBOARDING_PROGRESS_KEY,
    DEFAULT_PROGRESS,
  )

  const [step, setStep] = useState<StepId>(savedProgress.step)
  const [slideDir, setSlideDir] = useState<'left' | 'right' | 'none'>('none')
  const [visible, setVisible] = useState(true)

  // Wizard form state — seeded from localStorage progress
  const [displayName, setDisplayName] = useState(
    savedProgress.displayName || user?.displayName || user?.username || '',
  )
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformId[]>(
    savedProgress.selectedPlatforms,
  )
  const [connectedPlatforms, setConnectedPlatforms] = useState<PlatformId[]>(() => {
    // Merge saved + currently authenticated connections
    const authConnected = (user?.connections ?? []).map(
      (c) => c.platform.toLowerCase() as PlatformId,
    )
    const merged = new Set([...savedProgress.connectedPlatforms, ...authConnected])
    return Array.from(merged)
  })
  const [streamKey, setStreamKey] = useState<string | null>(savedProgress.streamKey)
  const [rtmpUrl, setRtmpUrl] = useState<string | null>(savedProgress.rtmpUrl)
  const [alertStyle, setAlertStyle] = useState(savedProgress.alertStyle)
  const [overlayTheme, setOverlayTheme] = useState(savedProgress.overlayTheme)
  const [chatMode, setChatMode] = useState(savedProgress.chatMode)

  // Persist progress to localStorage whenever state changes
  useEffect(() => {
    setSavedProgress({
      step,
      selectedPlatforms,
      connectedPlatforms,
      displayName,
      streamTitle: savedProgress.streamTitle,
      alertStyle,
      overlayTheme,
      chatMode,
      streamKey,
      rtmpUrl,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedPlatforms, connectedPlatforms, displayName, alertStyle, overlayTheme, chatMode, streamKey, rtmpUrl])

  const goTo = useCallback(
    (next: StepId) => {
      setSlideDir(next > step ? 'left' : 'right')
      setStep(next)
    },
    [step],
  )

  const advance = useCallback(() => {
    if (step < TOTAL_STEPS) goTo((step + 1) as StepId)
  }, [step, goTo])

  const finish = useCallback(
    (path: string) => {
      setVisible(false)
      setTimeout(() => {
        setComplete(true)
        navigate(path)
      }, 200)
    },
    [setComplete, navigate],
  )

  const dismiss = useCallback(() => {
    setVisible(false)
    setTimeout(() => setComplete(true), 200)
  }, [setComplete])

  const handlePlatformToggle = useCallback((id: PlatformId) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    )
  }, [])

  const handleKeysLoaded = useCallback((key: string, url: string) => {
    setStreamKey(key)
    setRtmpUrl(url)
  }, [])

  // Check sessionStorage for OAuth resume (user redirected mid-onboarding)
  useEffect(() => {
    const resumeStep = sessionStorage.getItem('kast_onboarding_resume_step')
    if (resumeStep) {
      sessionStorage.removeItem('kast_onboarding_resume_step')
      const parsed = parseInt(resumeStep, 10)
      if (!isNaN(parsed) && parsed >= 1 && parsed <= TOTAL_STEPS) {
        setStep(parsed as StepId)
      }
    }
  }, [])

  if (!visible) return null

  const currentProgress: OnboardingProgress = {
    step,
    selectedPlatforms,
    connectedPlatforms,
    displayName,
    streamTitle: savedProgress.streamTitle,
    alertStyle,
    overlayTheme,
    chatMode,
    streamKey,
    rtmpUrl,
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex: 60,
        backgroundColor: 'rgba(10, 10, 26, 0.92)',
        backdropFilter: 'blur(6px)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="First-run onboarding wizard"
    >
      {/* Card */}
      <div
        className="relative w-full rounded-2xl border shadow-2xl overflow-hidden"
        style={{
          maxWidth: 480,
          backgroundColor: '#0d0d20',
          borderColor: '#1e1e3a',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top accent bar */}
        <div
          className="h-1 w-full shrink-0"
          style={{ background: 'linear-gradient(90deg, #7c3aed, #a78bfa)' }}
          aria-hidden="true"
        />

        {/* Header row */}
        <div className="flex items-center justify-between px-6 pt-4 pb-3 shrink-0">
          <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
            {step < TOTAL_STEPS ? `Step ${step} of ${TOTAL_STEPS}` : 'Setup complete'}
          </span>
          <button
            type="button"
            onClick={dismiss}
            className="text-xs transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400 rounded"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
            aria-label="Skip onboarding"
          >
            Skip setup
          </button>
        </div>

        {/* Step bar */}
        <div className="px-6 pb-4 shrink-0">
          <StepBar current={step} total={TOTAL_STEPS} />
        </div>

        {/* Scrollable content area */}
        <div className="px-6 pb-6 overflow-y-auto flex-1">
          <SlidePane key={step} direction={slideDir}>
            {step === 1 && (
              <StepWelcome
                displayName={displayName}
                onDisplayNameChange={setDisplayName}
                selectedPlatforms={selectedPlatforms}
                onPlatformToggle={handlePlatformToggle}
                onNext={advance}
              />
            )}
            {step === 2 && (
              <StepConnectPlatforms
                selectedPlatforms={selectedPlatforms}
                connectedPlatforms={connectedPlatforms}
                onConnectedChange={setConnectedPlatforms}
                onNext={advance}
                onSkip={advance}
                onBack={() => goTo(1)}
              />
            )}
            {step === 3 && (
              <StepStreamKey
                streamKey={streamKey}
                rtmpUrl={rtmpUrl}
                onKeysLoaded={handleKeysLoaded}
                onNext={advance}
                onBack={() => goTo(2)}
              />
            )}
            {step === 4 && (
              <StepCustomize
                alertStyle={alertStyle}
                overlayTheme={overlayTheme}
                chatMode={chatMode}
                onAlertStyleChange={setAlertStyle}
                onOverlayThemeChange={setOverlayTheme}
                onChatModeChange={setChatMode}
                onNext={advance}
                onBack={() => goTo(3)}
              />
            )}
            {step === 5 && (
              <StepReady
                progress={currentProgress}
                onGoLive={() => finish('/live')}
                onDashboard={() => finish('/')}
              />
            )}
          </SlidePane>
        </div>
      </div>

      {/* Slide animation keyframes */}
      <style>{`
        @keyframes slide-in-left {
          from { opacity: 0; transform: translateX(32px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(-32px); }
          to   { opacity: 1; transform: translateX(0);     }
        }
        .animate-slide-in-left  { animation: slide-in-left  0.22s ease-out both; }
        .animate-slide-in-right { animation: slide-in-right 0.22s ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .animate-slide-in-left, .animate-slide-in-right {
            animation-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  )
}

// ─── Hook: should we show onboarding? ────────────────────────────────────────

/**
 * Returns whether the onboarding wizard should be shown automatically.
 * Rule: show on first login (onboarding not yet completed).
 *
 * `hasAnyStreams` kept for backward compatibility — when null (loading), don't show yet.
 */
export function useShowOnboarding(hasAnyStreams: boolean | null): {
  showOnboarding: boolean
  markOnboardingDone: () => void
} {
  const [complete, setComplete] = useLocalStorage<boolean>(ONBOARDING_COMPLETE_KEY, false)

  // hasAnyStreams===null means "still loading" — don't show yet
  const showOnboarding = !complete && hasAnyStreams === false

  const markOnboardingDone = useCallback(() => {
    setComplete(true)
  }, [setComplete])

  return { showOnboarding, markOnboardingDone }
}

/**
 * Clear onboarding completion state so the wizard re-appears.
 * Useful for a "Restart Setup" button in Settings.
 */
export function resetOnboarding(): void {
  localStorage.removeItem(ONBOARDING_COMPLETE_KEY)
  localStorage.removeItem(ONBOARDING_PROGRESS_KEY)
}
